/**
 * Shell UI state: which screen is active, whether the chrome (all floating
 * panels) is shown, and whether the ⌘K palette is open.
 *
 * This is deliberately separate from droneStore/missionStore: it holds no
 * telemetry and never updates at the broadcast rate. The screen registry
 * (features/workspace/register.tsx) and the shell (App.tsx) both read it, which
 * is why it lives in a store rather than in either of them — the switcher and
 * the screen it switches are two separate roots as far as the shell is
 * concerned.
 */

import { create } from 'zustand';

export type ScreenId =
  | 'command'
  | 'planning'
  | 'flights'
  | 'health'
  | 'supply'
  | 'data'
  | 'admin'
  | 'training';

interface WorkspaceState {
  screen: ScreenId;
  /** When false the chrome toggle has hidden every floating panel for a clean COP. */
  chrome: boolean;
  searchOpen: boolean;
  /** The keyboard-shortcut cheat sheet. */
  helpOpen: boolean;
  /** The AOR the operator is working in. All fleet/data is scoped to it. */
  aor: string;
  /** Have they flown in past the strategic entry globe into the command centre?
   *  Persistent — set on fly-in, cleared on return-to-orbit — so the command
   *  chrome does not flicker with a map's zoom on other screens. */
  entered: boolean;
  /** Tablet only: the one side rail open as a drawer over the map. A tablet
   *  has room for one 300px rail beside the dock, not two. */
  railDrawer: RailDrawer;
  /** Whether the bottom activity timeline is expanded. Lives here because the
   *  rails stop above it, and they are mounted by different screens. */
  timelineOpen: boolean;

  setScreen: (screen: ScreenId) => void;
  toggleChrome: () => void;
  setChrome: (chrome: boolean) => void;
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
  toggleHelp: () => void;
  closeHelp: () => void;
  setAor: (aor: string) => void;
  setEntered: (entered: boolean) => void;
  toggleRailDrawer: (drawer: Exclude<RailDrawer, null>) => void;
  setRailDrawer: (drawer: RailDrawer) => void;
  setTimelineOpen: (open: boolean) => void;
}

export type RailDrawer = 'fleet' | 'detail' | null;

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  screen: 'command',
  chrome: true,
  searchOpen: false,
  helpOpen: false,
  aor: 'JAZIRA',
  entered: false,
  railDrawer: null,
  timelineOpen: true,

  setScreen: (screen) => set({ screen, searchOpen: false, helpOpen: false }),
  toggleChrome: () => set((state) => ({ chrome: !state.chrome })),
  setChrome: (chrome) => set({ chrome }),
  openSearch: () => set({ searchOpen: true }),
  closeSearch: () => set({ searchOpen: false }),
  toggleSearch: () => set((state) => ({ searchOpen: !state.searchOpen })),
  toggleHelp: () => set((state) => ({ helpOpen: !state.helpOpen })),
  closeHelp: () => set({ helpOpen: false }),
  setAor: (aor) => set({ aor }),
  // Returning to the globe also returns to the Command screen.
  setEntered: (entered) => set(entered ? { entered } : { entered, screen: 'command' }),
  toggleRailDrawer: (drawer) =>
    set((state) => ({ railDrawer: state.railDrawer === drawer ? null : drawer })),
  setRailDrawer: (railDrawer) => set({ railDrawer }),
  setTimelineOpen: (timelineOpen) => set({ timelineOpen }),
}));

export function useScreen(): ScreenId {
  return useWorkspaceStore((state) => state.screen);
}
